import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CustomInput from "../../../shared/utils/Input";
import Button from "@mui/material/Button";
import {
  createVendor,
  updateFormField,
} from "../../../redux/slices/vendorFormSlice";
import { setFieldError } from "../../../redux/slices/vendorFormSlice";
import FileUpload from "../helperFunctions/fileUploader";
import { validateAllFields } from "./vendorValidation";
import { Snackbar, Alert } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { setToken } from "../../../redux/slices/vendorAuthSlice";
import { setVendorId } from "../../../redux/slices/vendorAuthSlice";
import { setIsRegistered } from "../../../redux/slices/vendorRegisterSlice";

const FinancialDetailsForm = ({ onBack }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { formData, errors, errorMessage } = useSelector(
    (state) => state.vendorForm
  );

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;

    const validationMessages = {
      // GSTIN: "GSTIN required.",
      // add more messages
      // typeOfBusiness: "Please select the type of business",
      // avgTurnoverLast3Years: "Please enter the average turnover",
      // productionArea: "Please enter the production area.",
      // numberOfEmployees: "Please enter number of employees.",
      // numberOfSkilledManpower: "Please enter number of skilled manpower.",
      // numberOfUnskilledManpower: "Please enter number of unskilled manpower.",
    };

    if (validationMessages[name]) {
      const isEmpty =
        typeof value === "string"
          ? value.trim() === ""
          : Array.isArray(value)
          ? value.length === 0
          : !value;

      if (isEmpty) {
        dispatch(setFieldError({ name, message: validationMessages[name] }));
      } else {
        dispatch(setFieldError({ name, message: "" }));
      }
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "scalingFactors") {
      dispatch(updateFormField({ name, value })); // value is already an array
      return;
    }

    if (name === "registeredInESIPF") {
      dispatch(updateFormField({ name, value }));
      return;
    }

    if (name === "bankAccountNumber") {
      // Allow only empty string (for clearing) or positive integers
      if (value === "" || /^[0-9]+$/.test(value)) {
        dispatch(updateFormField({ name, value }));
      }
      return;
    }

    // pan card number
    if (name === "PANNumber") {
      const inputRegex = /^[A-Z0-9]*$/; // Allow only A-Z and 0-9

      if (
        value === "" ||
        (inputRegex.test(value.toUpperCase()) && value.length <= 10)
      ) {
        dispatch(updateFormField({ name, value: value.toUpperCase() }));
      }
      return;
    }

    if (name === "bankIFSCCode") {
      if (
        value === "" ||
        (/^[A-Za-z0-9]+$/.test(value) && value.length <= 11)
      ) {
        dispatch(updateFormField({ name, value }));
      }
      return;
    }

    if (name === "bankAccountHolderName") {
      // Allow only empty string or alphabetic characters (with spaces)
      if (value === "" || /^[a-zA-Z\s]+$/.test(value)) {
        dispatch(updateFormField({ name, value }));
      }
      return;
    }

    if (name === "bankName") {
      if (!/^[a-zA-Z\s]*$/.test(value)) return;
      dispatch(updateFormField({ name, value }));
      return;
    }

    if (!value.startsWith(" ")) {
      dispatch(updateFormField({ name, value }));
    }
  };

  const handleSubmit = async () => {
    const isValid = validateAllFields(formData, dispatch);

    if (isValid) {
      const response = await dispatch(createVendor(formData));

      if (createVendor.fulfilled.match(response)) {
        const { vendor, token } = response.payload || {};

        if (vendor && token) {
          localStorage.setItem("vendorId", vendor._id);
          localStorage.setItem("vendorToken", token);
          dispatch(setVendorId(vendor._id));
          dispatch(setToken(token));
          dispatch(setIsRegistered(true));

          setSnackbar({
            open: true,
            message: "Registered successfully.",
            severity: "success",
          });

          setTimeout(() => {
            navigate("/vendorportal/dashboard");
          }, 1000);
        } else {
          setSnackbar({
            open: true,
            message: "Vendor creation failed due to missing token/vendor.",
            severity: "error",
          });
        }
      } else if (createVendor.rejected.match(response)) {
        // ✨ THIS IS THE MAIN FIX
        const errorMsg =
          response?.payload?.message ||
          response?.payload ||
          "Vendor creation failed";

        // Optional: check if it's GSTIN error and show inline
        if (
          typeof errorMsg === "string" &&
          errorMsg.toLowerCase().includes("gstin")
        ) {
          dispatch(setFieldError({ name: "GSTIN", message: errorMsg }));
        }

        setSnackbar({
          open: true,
          message: errorMsg,
          severity: "error",
        });
      }
    } else {
      setSnackbar({
        open: true,
        message: errorMessage || "Please fill all required fields correctly.",
        severity: "error",
      });
    }
  };

  return (
    <div className=" max-w-md ">
      <div
        onClick={onBack}
        className=" w-fit p-1 px-1.5 mb-4 rounded-full bg-gray-100 cursor-pointer"
      >
        <ArrowBackIcon fontSize="small" />
      </div>
      <div>
        <h1 className="text-3xl font-bold mb-4">Financial Details</h1>
        <p className="mb-6">
          Enter your financial details to keep the transactions transparent.
        </p>
      </div>
      <div
        className="overflow-auto"
        style={{ maxHeight: "calc(100vh - 200px)" }}
      >
        <Box
          sx={{
            flexGrow: 1,
          }}
        >
          <Grid container spacing={2}>
            {/* bank account holder name */}
            <Grid item xs={12}>
              <CustomInput
                placeholder="Bank holder name"
                title="Bank Account Holder Name "
                size="small"
                name="bankAccountHolderName"
                required
                value={formData?.bankAccountHolderName}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!errors.bankAccountHolderName}
                errorMessage={errors.bankAccountHolderName}
              />
            </Grid>
            {/* Bank account number */}
            <Grid item xs={12}>
              <CustomInput
                placeholder="Enter Bank account number"
                title="Bank Account Number "
                size="small"
                name="bankAccountNumber"
                required
                value={formData?.bankAccountNumber}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!errors.bankAccountNumber}
                errorMessage={errors.bankAccountNumber}
              />
            </Grid>
            {/* Enter bank name */}
            <Grid item xs={12}>
              <CustomInput
                placeholder="Enter bank name"
                title="Bank Name "
                size="small"
                name="bankName"
                value={formData?.bankName}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!errors.bankName}
                errorMessage={errors.bankName}
              />
            </Grid>
            {/* IFSC code  */}
            <Grid item xs={12}>
              <CustomInput
                placeholder="Enter IFSC code"
                title="IFSC "
                size="small"
                name="bankIFSCCode"
                value={formData?.bankIFSCCode}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!errors.bankIFSCCode}
                errorMessage={errors.bankIFSCCode}
              />
            </Grid>
            {/* Pan number */}
            <Grid item xs={12}>
              <CustomInput
                placeholder="Enter your PAN number."
                title="PAN "
                size="small"
                name="PANNumber"
                value={formData?.PANNumber}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!errors.PANNumber}
                errorMessage={errors.PANNumber}
              />
            </Grid>
            {/* cheque book */}
            <Grid item xs={12}>
              <FileUpload />
            </Grid>
          </Grid>
        </Box>

        <div className=" mt-4 flex justify-end gap-4">
          <Button onClick={handleSubmit} variant="contained">
            Submit
          </Button>
        </div>
      </div>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{
            backgroundColor:
              snackbar.severity === "success"
                ? "var(--third-color)"
                : "var(--heading-color)",
            color: "var(--secondary-color)",
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default FinancialDetailsForm;
